import { useWatch } from "react-hook-form";
import { useMemo } from "react";
import {
  CustomizableComponentConfiguration,
  commonSettings,
  fontSettings,
  kioskOptionItemSettings,
} from "../../../CustomComponents";

export function useKioskJamezzStylesComponentConfiguration(): CustomizableComponentConfiguration {
  const categoryOrientation = useWatch({ name: "extra_settings__orientation_of_categories" });
  const propposEnabled = useWatch({ name: "proppos_settings__enabled" });
  const piggyEnabled = useWatch({ name: "piggy__use_loyalty" });
  const salesareaType = useWatch({ name: "salesarea_type" });
  const wheelchairFriendly = useWatch({ name: "custom_data.kiosk.show_wheelchair_friendly_button" });

  return useMemo(() => {
    let kioskComponents: CustomizableComponentConfiguration = [];

    if (salesareaType !== "KIOSK") return kioskComponents;
    switch (categoryOrientation) {
      case "VERTICAL":
        kioskComponents = kioskComponents.concat([
          {
            className: ".JS-VerticalArticlegroupTab-Button",
            label: "Vertically orientated menu - Button",
            values: { backgroundColor: null, color: null, height: null, margin: null, minHeight: null },
          },
          {
            className: ".JS-VerticalArticlegroupTab-Root",
            label: "Vertically oriented menu - entire sidebar",
            values: {
              backgroundColor: null,
            },
          },
          {
            className: ".JS-VerticalArticlegroupTab-Text",
            label: "Text",
            values: { ...commonSettings },
          },
          {
            className: ".JS-VerticalArticlegroupTab-Image",
            label: "Image",
            values: { ...commonSettings, minHeight: null },
          },
          {
            className: ".JS-VerticalArticlegroupTab-ButtonTop",
            label: "Top tab bar button to scroll top",
            values: {
              color: null,
              backgroundColor: null,
            },
          },
          {
            className: ".JS-VerticalArticlegroupTab-Button-Selected",
            label: "Selected Articlegroup Tile",
            values: {
              borderColor: null,
              backgroundColor: null,
              color: null,
              textDecoration: null,
            },
          },
          {
            className: ".JS-VerticalArticlegroupTab-Text-Selected",
            label: "Selected Articlegroup Text",
            values: {
              ...commonSettings,
            },
          },
          {
            className: ".JS-VerticalArticlegroupTab-Home",
            label: "Home Button",
            values: {
              ...commonSettings,
            },
          },
          {
            className: ".JS-VerticalArticlegroupTab-Container",
            label: "Left menu container",
            values: {
              paddingLeft: null,
              paddingTop: null,
              paddingRight: null,
              paddingBottom: null,
              gap: null,
            },
          },
          {
            className: ".JS-VerticalArticlegroupTab-LogoWrapper",
            label: "Logo wrapper",
            values: {
              padding: null,
              paddingLeft: null,
              paddingTop: null,
              paddingRight: null,
              paddingBottom: null,
            },
          },
          {
            className: ".JS-VerticalArticlegroupTab-ButtonDown",
            label: "Top tab bar button to scroll bottom",
            values: {
              color: null,
              backgroundColor: null,
            },
          },
        ]);
        break;
      case "HORIZONTAL":
        kioskComponents = kioskComponents.concat([
          {
            className: ".JS-HorizontalArticlegroupTabBar-Root",
            label: "Horizontal Top Bar",
            values: { backgroundColor: null, color: null },
            JSImage: [{ name: "kioskv5_background_of_menu_page_header", res: "1080x132" }],
          },
          {
            className: ".JS-HorizontalArticlegroupTabBar-Text",
            label: "Top Tab Bar Text",
            values: { ...commonSettings },
          },
          {
            className: ".JS-HorizontalArticlegroupTabBar-Text-selected",
            label: "Top tab bar text when the tab is selected",
            values: { ...commonSettings },
          },
          {
            className: ".JS-HorizontalArticlegroupTabBar-Tab-Selected",
            label: "Top Tab Bar Button when it's selected",
            values: { ...commonSettings },
          },
          {
            className: ".JS-HorizontalArticlegroupTabBar-ButtonTop",
            label: "Top tab bar button to scroll left",
            values: {
              color: null,
              backgroundColor: null,
              marginTop: null,
              marginBottom: null,
              marginLeft: null,
              marginRight: null,
              zoom: null,
            },
          },
          {
            className: ".JS-HorizontalArticlegroupTabBar-ButtonDown",
            label: "Top tab bar button to scroll right",
            values: {
              color: null,
              backgroundColor: null,
              marginTop: null,
              marginBottom: null,
              marginLeft: null,
              marginRight: null,
              zoom: null,
            },
          },
        ]);
        break;
      case "MULTIPLE_LAYERS":
        kioskComponents = kioskComponents.concat([
          {
            className: ".JS-MultipleLayersMenuPage-Top",
            label: "Top Bar (Home button, Search, Valuta)",
            values: { backgroundColor: null, color: null, padding: null },
          },
          {
            className: ".JS-MultipleLayersBottomTabBar-Root",
            label: "Multi Layers (Articlegroup buttons)",
            values: { backgroundColor: null, color: null, padding: null },
          },
          {
            className: ".JS-HorizontalArticlegroupTabBar-Text",
            label: "Top Tab Bar Text",
            values: { color: null, fontFamily: null, fontWeight: null, fontSize: null },
          },
          {
            className: ".JS-MultipleLayersBottomTabBar-Children-Tab",
            label: "Multi Layers (navigation buttons)",
            values: {
              color: null,
              backgroundColor: null,
              padding: null,
              margin: null,
              fontStyle: null,
              fontFamily: null,
              fontWeight: null,
              fontSize: null,
            },
          },
        ]);
        break;
    }

    if (propposEnabled) {
      kioskComponents = kioskComponents.concat([
        {
          className: ".JS-PropposPage-MainTitle",
          label: "Main header of the proppos page",
          values: {
            backgroundColor: null,
            color: null,
            fontWeight: null,
            fontStyle: null,
            fontFamily: null,
            fontSize: null,
          },
        },
        {
          className: ".JS-PropposPage-ScanButton",
          label: "The button to add trays to shopping cart and other messages",
          values: {
            backgroundColor: null,
            color: null,
            height: null,
            width: null,
            margin: null,
            padding: null,
          },
        },
        {
          className: ".JS-PropposPage-Page",
          label: "The proppos page",
          values: {
            backgroundColor: null,
          },
        },
      ]);
    }
    if (piggyEnabled) {
      kioskComponents = kioskComponents.concat([
        {
          className: ".JS-ArticleContent-PiggyRewardBadge",
          label: "The badge shown on products that can be claimed as a piggy reward",
          values: {
            color: null,
          },
        },
        {
          className: ".JS-PiggyScanDialog-Root",
          label: "The popup telling you to scan your piggy loyalty card - container",
          values: { ...commonSettings },
        },
        {
          className: ".JS-PiggyScanDialog-LaterButton",
          label: '"not now" Button for the scan piggy loyalty card',
          values: { ...commonSettings },
        },
        {
          className: ".JS-KioskTopBar-Piggy-ScanDialogButton",
          label: "Icon button to show the piggy scan dialog button",
          values: {
            ...commonSettings,
          },
        },
        {
          className: ".JS-PiggyScanDialog-Title",
          label: "The header/title of the piggy scan dialog",
          values: { ...commonSettings },
        },
        {
          className: ".JS-Piggy-RewardsPage",
          label: "Piggy rewards page",
          values: {
            ...commonSettings,
          },
        },
        {
          className: ".JS-PiggyScanDialog-Body",
          label: "The body text of the piggy scan dialog",
          values: { ...commonSettings },
        },
        {
          className: ".JS-OrderPage-LeatVoucherText",
          label: "The text to show your used vouchers",
          values: { ...commonSettings, display: null },
        },
        {
          className: ".JS-OrderPage-LeatVoucherAmount",
          label: "The text to show the number of used vouchers",
          values: { ...commonSettings, display: null },
        },
        {
          className: ".JS-OrderPage-LeatVoucherDivider",
          label: "The line underneath the text to show your used vouchers",
          values: { backgroundColor: null, display: null },
        },
      ]);
    }
    if (wheelchairFriendly) {
      kioskComponents.push({
        className: ".JS-WheelchairFriendly-Button",
        label: "Wheelchair friendly button",
        values: { color: null, backgroundColor: null, translate: null, scale: null },
      });
      kioskComponents.push({
        className: ".JS-WheelchairFriendly-Text",
        label: "Wheelchair friendly text",
        values: { display: null, color: null, backgroundColor: null, translate: null, scale: null },
      });
    }

    kioskComponents = kioskComponents.concat([
      {
        className: ".JS-CustomFields-Step-Label",
        label: "A stepper label",
        values: {
          color: null,
        },
      },
      {
        className: ".JS-OrderArticleSelectorPage-HeaderWrapper",
        label: "The top bar in the order article selector dialog",
        values: {
          backgroundColor: null,
          color: null,
        },
      },
      {
        className: ".JS-OrderArticleSelectorPage-Root",
        label: "Order article dialog",
        values: {
          padding: null,
          margin: null,
        },
      },
      {
        className: ".JS-ArticlegroupContent-StickyBox",
        label: "StickyBox",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-LanguageSelector-Root",
        label: "Language Selector",
        values: {
          translate: null,
          display: null,
        },
      },
      {
        className: ".JS-LanguageSelector-Text",
        label: "Language Text",
        values: { ...commonSettings },
      },
      {
        className: ".JS-LanguageSelector-Text-Selected",
        label: "Language Text Selected",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-SubArticlegroupTabBar-Tab",
        label: "Sub Articlegroup Tab",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-SubArticlegroupTabBar-Tab-selected",
        label: "Sub Articlegroup Tab Selected",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-ShoppingCartButtonCounter-Count",
        label: "ShoppingCart Counter",
        values: {
          backgroundColor: null,
          color: null,
        },
      },
      {
        className: ".JS-PiggyScanLoyalty-Button",
        label: "Piggy scan loyalty",
        values: { backgroundColor: null, borderColor: null, borderRadius: null, display: null },
      },
      {
        className: ".JS-PiggyScanLoyalty-Button-Text",
        label: "Piggy scan loyalty text",
        values: { ...commonSettings },
      },
      {
        className: ".JS-ArticleContent-PlusButton",
        label: "Increase count of an article button on an article in the order page",
        values: {
          color: null,
        },
      },
      {
        className: ".JS-ArticleContent-MinusButton",
        label: "Decrease count or delete item of an article button on an article in the order page",
        values: {
          color: null,
        },
      },
      {
        className: ".JS-ArticleContent-ChangeOrderButton",
        label: "Button to change an order for an article on the order page",
        values: {
          color: null,
          backgroundColor: null,
        },
      },
      {
        className: ".JS-ArticleContent-Root",
        label: "Article on the order page",
        values: {
          backgroundColor: null,
        },
      },
      {
        className: ".JS-OrderArticleSelectorPage-Option",
        label: "Options on the order article selector page",
        values: {
          color: null,
          backgroundColor: null,
          fontFamily: null,
          fontStyle: null,
          fontWeight: null,
          fontSize: null,
        },
      },
      {
        className: ".JS-OrderArticleSelectorPage-Option-Chip",
        label: "Selected Options on the article selector page",
        values: {
          ...fontSettings,
          backgroundColor: null,
          borderRadius: null,
          height: null,
        },
      },
      {
        className: ".JS-ArticleContent-Option",
        label: "Options on articles in the order page",
        values: {
          color: null,
          backgroundColor: null,
          fontFamily: null,
          fontStyle: null,
          fontWeight: null,
          fontSize: null,
        },
      },
      {
        className: ".JS-WideShoppingCartButton-ButtonBase",
        label: "The button in the wide shopping cart drawer",
        values: {
          ...commonSettings,
          height: null,
          width: null,
          translate: null,
          scale: null,
          flexDirection: null,
        },
      },
      {
        className: ".JS-WideShoppingCartButton-ButtonText",
        label: "The text in the button in the wide shopping cart drawer",
        values: {
          ...commonSettings,
          translate: null,
        },
      },
      {
        className: ".JS-WideShoppingCartButton-Image",
        label: "The Image in the button of the wide shopping cart",
        values: {
          ...commonSettings,
          translate: null,
          position: null,
          scale: null,
          width: null,
          height: null,
        },
      },
      {
        className: ".JS-WideShoppingCartButton-ImageWrapper",
        label: "The Image Wrapper in the button of the wide shopping cart",
        values: {
          width: null,
          height: null,
        },
      },
      {
        className: ".JS-WideShoppingCartButton-ButtonTextPrice",
        label: "The price in the button in the wide shopping cart drawer",
        values: {
          ...commonSettings,
          translate: null,
        },
      },
      {
        className: ".JS-WideShoppingCartButton-Price",
        label: "The price in the wide shopping cart drawer",
        values: {
          ...commonSettings,
          translate: null,
        },
      },
      {
        className: ".JS-WideShoppingCartButton-Root",
        label: "Shopping cart button wide, drawer",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemOptional-Root",
        label: "Option item at most 1",
        values: {
          backgroundColor: null,
          color: null,
        },
      },
      {
        className: ".JS-OptionItemOptional-Button-Selected",
        label: "Option item at most 1 when selected",
        values: {
          backgroundColor: null,
          color: null,
        },
      },
      {
        className: ".JS-OptionItemRadio-Root",
        label: "Option item at most 1",
        values: {
          backgroundColor: null,
          color: null,
        },
      },
      {
        className: ".JS-OptionItemRadio-Price",
        label: "Option item price",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemOptional-Price",
        label: "Option item price",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-Price",
        label: "Option item price",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemRadio-Price-selected",
        label: "Option item price selected",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemOptional-Price-selected",
        label: "Option item price selected",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-Price-selected",
        label: "Option item price selected",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemRadio-Text",
        label: "Option item text",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemOptional-Text",
        label: "Option item text",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-Text",
        label: "Option item text",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemRadio-Text-selected",
        label: "Option item text selected",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemOptional-Text-selected",
        label: "Option item text selected",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-Text-selected",
        label: "Option item text selected",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OptionItemRadio-Button-Selected",
        label: "Option item at most 1 when selected",
        values: {
          backgroundColor: null,
          color: null,
        },
      },
      {
        className: ".JS-OptionItemGeneric-Root",
        label: "Generic option item box, use for background and border styling.",
        values: {
          ...kioskOptionItemSettings,
          ...fontSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-Root",
        label: "Countable option item box, use for background and border styling.",
        values: {
          ...kioskOptionItemSettings,
          fontFamily: null,
        },
      },
      {
        className: ".JS-OptionItemCountable-ControlBox",
        label: "Plus minus box",
        values: {
          ...kioskOptionItemSettings,
          ...fontSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-ControlBox-selected",
        label: "Plus minus box selected",
        values: {
          ...kioskOptionItemSettings,
          ...fontSettings,
        },
      },
      {
        className: ".JS-OptionItemSwitch-Root",
        label: "Generic option item box, use for background and border styling.",
        values: {
          ...kioskOptionItemSettings,
          ...fontSettings,
        },
      },
      {
        className: ".JS-OptionItemRadio-Root",
        label: "Generic option item box, use for background and border styling.",
        values: {
          ...kioskOptionItemSettings,
          ...fontSettings,
        },
      },
      {
        className: ".JS-ArticlegroupContent-Wrapper",
        label: "Wrapper for the title and availability of a menu, use for centering",
        values: {
          justifyContent: null,
        },
      },
      {
        className: ".JS-ArticlegroupContent-Name",
        label: "Articlegroup Title",
        values: { ...commonSettings },
      },
      {
        className: ".JS-ArticleContent-PriceWrapper",
        label: "Article price wrapper and image",
        JSImage: [{ name: "kioskv5_background_of_price_label", res: "unknown" }],
        values: {
          color: null,
        },
      },
      {
        className: ".JS-ArticleContent-Wrapper1",
        label: "Inner wrapper for an article",
        values: {
          backgroundColor: null,
          marginTop: null,
          marginBottom: null,
          marginRight: null,
          marginLeft: null,
          alignItems: null,
        },
      },
      {
        className: ".JS-SmallShoppingCartButton-TopBox",
        label: "Shopping cart logo image",
        JSImage: [{ name: "kioskv5_logo_of_shopping_cart_button", res: "186x87" }],
      },
      {
        className: ".JS-SmallShoppingCartButton-InnerWrapper",
        label: "Shopping cart background image",
        JSImage: [{ name: "kioskv5_background_of_shopping_cart_button", res: "250x250" }],
      },
      {
        className: ".JS-ReturnPage-Root",
        label: "Return page background",
        JSImage: [{ name: "kioskv5_background_of_return_page", res: "1080x1920" }],
      },
      {
        className: ".JS-CheckoutPage-Root",
        label: "Checkout page background",
        JSImage: [{ name: "kioskv5_background_of_checkout_page", res: "1080x1920" }],
        values: {
          backgroundColor: null,
        },
      },
      {
        className: ".JS-OrderPage-Root",
        label: "Order page background",
        JSImage: [{ name: "kioskv5_background_of_order_page", res: "1080x1920" }],
        values: {
          backgroundColor: null,
        },
      },
      {
        className: ".JS-OrderArticleSelectorPage-root",
        label: "Article selector background image",
        JSImage: [{ name: "kioskv5_background_of_order_article_selector_page", res: "1080x1920" }],
      },
      {
        className: ".JS-OrderArticleSelectorPage-BackgroundColor",
        label: "Article selector background color",
        values: {
          backgroundColor: null,
        },
      },
      {
        className: ".JS-MultipleLayersMenuPage-Root",
        label: "Menu background",
        JSImage: [{ name: "kioskv5_background_of_menu_page_content", res: "1080x1920" }],
      },
      {
        className: ".JS-HorizontalMenuPage-Root",
        label: "Kiosk menu background",
        JSImage: [{ name: "kioskv5_background_of_menu_page_content", res: "1080x1920" }],
        values: { backgroundColor: null },
      },
      {
        className: ".JS-HomeScreen-Root",
        label: "Kiosk home page background",
        JSImage: [
          {
            name: "kioskv5_background_of_home_page_open",
            res: "1080x1920",
          },
          {
            name: "kioskv5_background_of_home_page_closed",
            res: "1080x1920",
          },
        ],
      },
      {
        className: ".JS-OptionOrderGroups-PlusButton",
        label: "Increment button for options",
        values: {
          color: null,
          backgroundColor: null,
          fontFamily: null,
          fontStyle: null,
          fontWeight: null,
          fontSize: null,
          borderRadius: null,
          padding: null,
          margin: null,
          width: null,
          height: null,
          visibility: null,
          boxShadow: null,
        },
      },
      {
        className: ".JS-OptionOrderGroups-MinusButton",
        label: "Increment button for options",
        values: {
          color: null,
          backgroundColor: null,
          fontFamily: null,
          fontStyle: null,
          fontWeight: null,
          fontSize: null,
          borderRadius: null,
          padding: null,
          margin: null,
          width: null,
          height: null,
          visibility: null,
          boxShadow: null,
        },
      },
      {
        className: ".JS-CheckoutPage-CustomField-Content-Wrapper",
        label: "CustomFieldContent-Wrapper",
        values: {
          padding: null,
          margin: null,
          backgroundColor: null,
          height: null,
          width: null,
        },
      },
      {
        className: ".JS-CustomFields-Step",
        label: "CustomFields stepper step indicator circles",
        values: {
          color: null,
          backgroundColor: null,
        },
      },
      {
        className: ".JS-CustomFields-Step-Active",
        label: "CustomFields stepper step indicator circle when the user is on this step",
        values: {
          color: null,
          backgroundColor: null,
        },
      },
      {
        className: ".JS-CustomFields-Step-Completed",
        label: "Customfields stepper step indicator circle when the field is correctly filled in",
        values: {
          fill: null,
        },
      },
      {
        className: ".JS-CheckoutPage-Header-CheckoutOrOrder",
        label:
          "The header of the page, its text is 'checkout' or 'send order' depending on whether payDirect is enabled or not",
        values: {
          backgroundColor: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
        },
      },
      {
        className: ".JS-CustomFields-PreviousNextButtons-Disabled",
        label: "When the previous and next buttons for the customfields stepper are disabled",
        values: {
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          color: null,
          backgroundColor: null,
          border: null,
        },
      },
      {
        className: ".JS-CustomFields-NextButton",
        label: "The next button for the customfields stepper on the customfields page",
        values: {
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          color: null,
          backgroundColor: null,
        },
      },
      {
        className: ".JS-CustomFields-NextButton-Disabled",
        label: "The next button for the customfields stepper on the customfields page when it is disabled",
        values: {
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          color: null,
          backgroundColor: null,
        },
      },
      {
        className: ".JS-CustomFields-PreviousButton",
        label: "The previous button for the customfields stepper on the customfields page",
        values: {
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          color: null,
          backgroundColor: null,
        },
      },
      {
        className: ".JS-CustomFields-PreviousButton-Disabled",
        label: "The previous button for the customfields stepper on the customfields page when it is disabled",
        values: {
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          color: null,
          backgroundColor: null,
        },
      },
      {
        className: ".JS-CustomFields-PreviousNextButtons",
        label: "The previous and next buttons for the customfields stepper on the customfields page",
        values: {
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          color: null,
          backgroundColor: null,
          borderRadius: null,
        },
      },
      {
        className: ".JS-currency-symbol-OrderArticleSelectorPage-price",
        label: "Currency symbol in order article selector page",
        values: {
          display: null,
          color: null,
        },
      },
      {
        className: ".JS-currency-value-OrderArticleSelectorPage-price",
        label: "Currency value in order article selector page",
        values: {
          color: null,
        },
      },
      {
        className: ".JS-currency-value",
        label: "Currency value",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-currency-symbol",
        label: "Currency symbol",
        values: {
          ...commonSettings,
        },
      },
      {
        className: ".JS-OrderArticleSelectorPage-OptionGroupContainer",
        label: "The container where options are shown",
        values: {
          backgroundColor: null,
        },
      },
      {
        className: ".JS-CloseDialogButton-Root",
        label: "Close Dialog Button",
        values: { backgroundColor: null, color: null, display: null, scale: null },
      },
      {
        className: ".JS-CloseDialogButton-Icon",
        label: "Close Dialog Button icon",
        values: { backgroundColor: null, color: null, display: null },
      },
      {
        className: ".JS-BackDialogButton",
        label: "Back Dialog Button",
        values: { backgroundColor: null, color: null, display: null },
      },
      {
        className: ".JS-CampaignBanner-Root",
        label: "CampaignBanner",
        values: { height: null, width: null, position: null, ...commonSettings },
      },
      {
        className: ".JS-OrderModeSelector-ButtonBase",
        label: "OrderModeSelector Button",
        values: {
          height: null,
          width: null,
          backgroundColor: null,
          display: null,
          padding: null,
          margin: null,
          borderRadius: null,
        },
      },
      {
        className: ".JS-OrderModeSelector-ButtonBase-Text",
        label: "OrderModeSelector Button Text",
        values: {
          backgroundColor: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
        },
      },
      {
        className: ".JS-LanguageSelector-Selected",
        label: "Language Selector Button - Selected State",
        values: {
          borderColor: null,
          backgroundColor: null,
        },
      },
      {
        className: ".JS-MenuArticleGroups-Root",
        label: "Menu",
        values: {
          backgroundColor: null,
        },
      },
      {
        className: ".JS-HomeScreen-Content-KioskClosedMessage",
        label: "Kiosk Closed Message",
        values: {
          display: null,
          backgroundColor: null,
        },
      },
      {
        className: ".JS-HomeScreen-Content-KioskClosedMessage-Text",
        label: "Kiosk Closed Message text",
        values: {
          ...fontSettings,
        },
      },
      {
        className: ".JS-HomeScreen-Content-KioskDefaultClosedMessage",
        label: "Kiosk Default Closed Message",
        values: {
          display: null,
          backgroundColor: null,
        },
      },
      {
        className: ".JS-HomeScreen-Content-KioskDefaultClosedMessage-Text",
        label: "Kiosk Default Closed Message Text",
        values: {
          ...fontSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-plus-minus",
        label: "+ and - buttons",
        values: {
          ...fontSettings,
        },
      },
      {
        className: "JS-OptionItemCountable-plus-minus-selected",
        label: "+ and - buttons selected",
        values: {
          ...fontSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-count",
        label: "The count",
        values: {
          ...fontSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-count-selected",
        label: "The count selected",
        values: {
          ...fontSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-plus-minus-text",
        label: "Minus & Plus text",
        values: {
          ...fontSettings,
        },
      },
      {
        className: ".JS-OptionItemCountable-plus-minus-text-selected",
        label: "Minus & Plus text selected",
        values: {
          ...fontSettings,
        },
      },
      {
        className: ".JS-HomeScreen-LanguageSelector-Wrapper",
        label: "JS-HomeScreen-LanguageSelector-Wrapper",
        values: {
          borderColor: null,
          backgroundColor: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          margin: null,
          padding: null,
          width: null,
          height: null,
        },
      },
      {
        className: ".JS-OptionOrderGroups-Pagination-Bullet",
        label: "A pagination bullet on the options select page",
        values: {
          backgroundColor: null,
          color: null,
        },
      },
      {
        className: ".JS-OptionOrderGroups-Pagination-Bullet--Current",
        label: "The active pagination bullet on the options select page",
        values: {
          backgroundColor: null,
          color: null,
        },
      },
      {
        className: ".JS-OptionOrderGroups-AddButton",
        label: "JS-OptionOrderGroups-AddButton",
        values: {
          borderColor: null,
          backgroundColor: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          margin: null,
          padding: null,
          width: null,
          height: null,
          borderRadius: null,
        },
      },
      // { className: ".JS-HomeScreen-Content", label: ".JS-HomeScreen-Content", values: { margin: null, padding: null } },
      {
        className: ".JS-OrderModeSelector-Root",
        label: ".JS-OrderModeSelector-Root",
        values: { margin: null, padding: null, transform: null, translate: null },
      },
      {
        className: ".JS-OrderPage-GoToCheckoutButton",
        label: "The 'checkout' button on the order page",
        values: {
          borderColor: null,
          backgroundColor: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          margin: null,
          padding: null,
          width: null,
          height: null,
          borderRadius: null,
        },
      },
      {
        className: ".JS-AreYouSureDialog-Text",
        label: "Are you sure dialog text.",
        values: {
          ...fontSettings,
          textAlign: null,
        },
      },
      {
        className: ".JS-OrderPage-ContinueShopping",
        label: "The 'order more' button on the order page",
        values: {
          borderColor: null,
          backgroundColor: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          margin: null,
          padding: null,
          width: null,
          height: null,
          borderRadius: null,
        },
      },
      {
        className: ".JS-OrderPage-ContinueShopping-PlayArrow",
        label: "Backward arrow order more button",
        values: {
          display: null,
        },
      },
      {
        className: ".JS-OrderPage-TotalCount",
        label: "JS-OrderPage-TotalCount",
        values: {
          borderColor: null,
          backgroundColor: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          margin: null,
          padding: null,
          width: null,
          height: null,
        },
      },
      {
        className: ".JS-OrderPage-TotalAmount",
        label: "JS-OrderPage-TotalAmount",
        values: {
          borderColor: null,
          backgroundColor: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          margin: null,
          padding: null,
          width: null,
          height: null,
          display: null,
        },
      },
      {
        className: ".JS-CheckoutPage-TotalAmount",
        label: "JS-CheckoutPage-TotalAmount",
        values: {
          borderColor: null,
          backgroundColor: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          margin: null,
          padding: null,
          width: null,
          height: null,
        },
      },
      {
        className: ".JS-SendOrderButton-Root",
        label: "JS-SendOrderButton-Root",
        values: {
          borderColor: null,
          backgroundColor: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontFamily: null,
          fontSize: null,
          margin: null,
          padding: null,
          width: null,
          height: null,
          borderRadius: null,
        },
      },
      {
        className: ".JS-SendOrderButton-PlayArrow",
        label: "Forward arrow checkout button",
        values: {
          display: null,
        },
      },
      {
        className: ".JS-Piggy-ViewAllRewardsLink",
        label: "View all rewards piggy link",
        values: { color: null, fontFamily: null, fontStyle: null, fontWeight: null, fontSize: null, display: null },
      },
      {
        className: ".JS-HorizontalArticlegroupTabBar-SearchWrapper",
        label: "Search wrapper",
        values: {
          backgroundColor: null,
          paddingTop: null,
          paddingBottom: null,
          marginTop: null,
          marginBottom: null,
          marginLeft: null,
          marginRight: null,
          zoom: null,
        },
      },
      {
        className: ".JS-HorizontalArticlegroupTabBar-Logo",
        label: "Topbar logo",
        values: {
          height: null,
          width: null,
          zoom: null,
          paddingTop: null,
          paddingBottom: null,
          paddingLeft: null,
          paddingRight: null,
          marginTop: null,
          marginBottom: null,
          marginLeft: null,
          marginRight: null,
        },
      },
      {
        className: ".JS-KioskTopBar-SearchWrapper",
        label: "Search wrapper",
        values: {
          zoom: null,
          gap: null,
          paddingTop: null,
          paddingBottom: null,
          paddingLeft: null,
          paddingRight: null,
          marginTop: null,
          marginBottom: null,
          marginLeft: null,
          marginRight: null,
        },
      },
      {
        className: ".JS-LanguageFlag-icon",
        label: "Language flag",
        values: {
          width: null,
          height: null,
          zoom: null,
          paddingTop: null,
          paddingBottom: null,
          paddingLeft: null,
          paddingRight: null,
          marginTop: null,
          marginBottom: null,
          marginLeft: null,
          marginRight: null,
          borderRadius: null,
        },
      },
      {
        className: ".JS-HorizontalArticlegroupTabBar-TabScroller",
        label: "Top bar tab scroller",
        values: {
          backgroundColor: null,
          paddingTop: null,
          paddingBottom: null,
          zoom: null,
          marginLeft: null,
          marginRight: null,
          marginTop: null,
          marginBottom: null,
          borderRadius: null,
        },
      },
      {
        className: ".JS-HorizontalArticlegroupTabBar-ArticlegroupImage",
        label: "Top bar images",
        values: {
          width: null,
          height: null,
          paddingTop: null,
          paddingLeft: null,
          paddingRight: null,
          paddingBottom: null,
          zoom: null,
          boxShadow: null,
          backgroundColor: null,
          borderRadius: null,
        },
      },
      {
        className: ".JS-DisplayConfiguredMeal-Root",
        label: "Meal configurator wrapper",
        values: { height: null, width: null, display: null, zoom: null },
      },
      {
        className: ".JS-ArticlegroupContent-Image",
        label: "Articlegroup banner",
        values: {
          width: null,
          height: null,
          zoom: null,
          marginTop: null,
          marginBottom: null,
          marginLeft: null,
          marginRight: null,
          margin: null,
          borderRadius: null,
          boxShadow: null,
        },
      },
      {
        className: ".JS-OptionGroupContent-Divider",
        label: "Divider",
        values: {
          borderWidth: null,
          borderColor: null,
          boxShadow: null,
          marginTop: null,
          marginBottom: null,
          marginRight: null,
          marginLeft: null,
        },
      },
    ]);

    return kioskComponents;
  }, [salesareaType, categoryOrientation, propposEnabled, piggyEnabled, wheelchairFriendly]);
}
